import { ComponentProps, createContext, useContext, useRef } from "react";
import { Transition, TransitionStatus } from "react-transition-group";
import { twMerge } from "tailwind-merge";
import { Portal } from ".";
import { ToggleProps, TransitionClasses } from "../types";

type DrawerContextType = { transitionState: TransitionStatus };
type DrawerProps = ToggleProps & Omit<ComponentProps<"div">, keyof ToggleProps>;

const DrawerContext = createContext({} as DrawerContextType);

function Drawer({
  isOpen,
  toggle,
  className = "",
  children,
  ...props
}: DrawerProps) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const transitionClasses: TransitionClasses = {
    entering: "opacity-100 pointer-events-auto active",
    entered: "opacity-100 pointer-events-auto active",
    exiting: "opacity-0 pointer-events-none",
    exited: "opacity-0 pointer-events-none",
    unmounted: "",
  };
  return (
    <Transition nodeRef={divRef} in={isOpen} timeout={300} unmountOnExit>
      {(state) => (
        <Portal>
          <div
            ref={divRef}
            className={twMerge(
              "drawer fixed size-full inset-0 flex flex-col z-30 bg-black/20 transition-opacity",
              transitionClasses[state],
              className
            )}
            {...props}
          >
            <button
              type="button"
              onClick={toggle}
              className="absolute inset-0 size-full opacity-0 cursor-default"
            />
            <DrawerContext.Provider value={{ transitionState: state }}>
              {children}
            </DrawerContext.Provider>
          </div>
        </Portal>
      )}
    </Transition>
  );
}
function DrawerMenu({
  children,
  className = "",
  ...props
}: ComponentProps<"div">) {
  const { transitionState } = useContext(DrawerContext);
  const transitionClasses: TransitionClasses = {
    entering: "translate-y-0",
    entered: "translate-y-0",
    exiting: "translate-y-full",
    exited: "translate-y-full",
    unmounted: "",
  };

  return (
    <div
      className={twMerge(
        "required: w-full h-fit flex flex-col bg-white rounded-t transition-transform divide-y divide-[#ECEFF2] max-w-screen-sm mx-auto mt-auto z-[1]",
        transitionClasses[transitionState],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
function DrawerHeader({
  className = "",
  children,
  ...props
}: ComponentProps<"div">) {
  return (
    <div className={twMerge("w-full px-5 py-7", className)} {...props}>
      {children}
    </div>
  );
}
function DrawerBody({
  className = "",
  children,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      className={twMerge("flex-1 overflow-auto px-5 py-7", className)}
      {...props}
    >
      {children}
    </div>
  );
}
function DrawerFooter({
  className = "",
  children,
  ...props
}: ComponentProps<"div">) {
  return (
    <div className={twMerge("w-full px-5 py-7", className)} {...props}>
      {children}
    </div>
  );
}
Drawer.Menu = DrawerMenu;
Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;
export default Drawer;
